/**
 * Soma Psychos — article page effects (desktop only)
 *
 * Loaded from sections/main-blog-post.liquid. Companion CSS lives in
 * assets/base.css under "ARTICLE PAGE — zine layout".
 *
 * Adds .anim-ready to <html> so the hidden/initial states in CSS apply only
 * when this script runs; without JS the article renders fully static.
 * Nothing here runs on phones (< 750px) or under prefers-reduced-motion.
 *
 * Effects: hero circle morph on scroll, hero tilt + spotlight on hover,
 * drop cap reveal, section-divider draw above headings.
 */
(() => {
  'use strict';

  const main = document.querySelector('main[data-template="article"]');
  if (!main) return;
  if (window.innerWidth < 750) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.documentElement.classList.add('anim-ready');

  const content = main.querySelector('.blog-post-content.rte rte-formatter');
  const hero = main.querySelector('.blog-post-featured-image__image');
  const heroWrap = main.querySelector('.blog-post-featured-image');

  /* Drop cap: wrap the first letter of the first paragraph. */
  const firstP = content?.querySelector(':scope > p:first-of-type');
  if (firstP && !firstP.querySelector('.drop-cap')) {
    const text = firstP.firstChild;
    if (text?.nodeType === Node.TEXT_NODE) {
      const m = text.nodeValue.match(/^(\s*)(\S)/);
      if (m) {
        const cap = document.createElement('span');
        cap.className = 'drop-cap';
        cap.textContent = m[2];
        text.nodeValue = text.nodeValue.slice(m[0].length);
        firstP.insertBefore(cap, text);
        if (m[1]) firstP.insertBefore(document.createTextNode(m[1]), cap);
      }
    }
  }

  /* Hero circle morph: --hero-progress goes 0 → 1 as the hero scrolls out. */
  if (hero) {
    let raf = null;
    const update = () => {
      raf = null;
      const rect = hero.getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, -rect.top) / ((rect.height || 1) * 1.4));
      hero.style.setProperty('--hero-progress', progress.toFixed(4));
    };
    const onScroll = () => { if (raf === null) raf = requestAnimationFrame(update); };
    update();
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
  }

  /* Hero tilt + spotlight, pointer devices only. */
  if (heroWrap && hero && matchMedia('(pointer: fine)').matches) {
    let raf = null;
    let x = 0;
    let y = 0;
    const update = () => {
      raf = null;
      const rect = heroWrap.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const rx = (x - rect.left) / rect.width;
      const ry = (y - rect.top) / rect.height;
      hero.style.setProperty('--tilt-y', ((rx - 0.5) * 12).toFixed(2) + 'deg');
      hero.style.setProperty('--tilt-x', (-(ry - 0.5) * 12).toFixed(2) + 'deg');
      heroWrap.style.setProperty('--spot-x', (rx * 100).toFixed(2) + '%');
      heroWrap.style.setProperty('--spot-y', (ry * 100).toFixed(2) + '%');
    };
    heroWrap.addEventListener('mouseenter', () => heroWrap.classList.add('is-hover'));
    heroWrap.addEventListener('mousemove', (e) => {
      x = e.clientX;
      y = e.clientY;
      if (raf === null) raf = requestAnimationFrame(update);
    }, { passive: true });
    heroWrap.addEventListener('mouseleave', () => {
      heroWrap.classList.remove('is-hover');
      hero.style.setProperty('--tilt-x', '0deg');
      hero.style.setProperty('--tilt-y', '0deg');
    });
  }

  /* Scroll-in reveals: drop cap once, divider lines above section headings. */
  const dividers = content ? [...content.querySelectorAll(':scope > :is(h2, h3):not(:first-child)')] : [];
  const targets = [firstP, ...dividers].filter(Boolean);
  if (targets.length) {
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in-view');
            io.unobserve(e.target);
          }
        }
      }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });
      targets.forEach((t) => io.observe(t));
    } else {
      targets.forEach((t) => t.classList.add('is-in-view'));
    }
  }
})();
